// =============================================================================
// REFLECTION PROMPTS - Zichtbaarheid & Markt
// =============================================================================
//
// Neutrale reflectievragen om externe feedback en markttoetsing te verkennen.
// GEEN diagnose, GEEN labels, GEEN conclusies.
//
// =============================================================================

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

export type ReflectionVariant = 'practical' | 'neutral' | 'reflective';

export interface ReflectionQuestion {
  id: string;
  variants: {
    practical: string;
    neutral: string;
    reflective: string;
  };
}

export interface ReflectionBlock {
  title: string;
  questions: ReflectionQuestion[];
}

// -----------------------------------------------------------------------------
// REFLECTION QUESTIONS - Zichtbaarheid & Markt
// -----------------------------------------------------------------------------

const INTRO_TEXT = 
  "De volgende vragen helpen je om te kijken hoeveel externe input en spiegeling er nu al is rondom dit doel.";

const INTROSPECTION_INTRO_TEXT = 
  "Eerlijkheid naar jezelf is essentieel. Deze antwoorden helpen bij het automatisch genereren van realistische mijlpalen.";

const OUTRO_TEXT = 
  "Neem even de tijd om te voelen of dit genoeg externe spiegeling geeft voor waar je nu staat.";

// Vraagblok A — Contact met buitenwereld
const BLOCK_A_QUESTIONS: ReflectionQuestion[] = [
  {
    id: 'a1_external_contact',
    variants: {
      practical: "Wie heeft dit idee of doel de afgelopen tijd gezien of besproken met jou?",
      neutral: "Met wie deel je momenteel dit doel of deze plannen?",
      reflective: "Wie staat nu buiten jou en kijkt mee?"
    }
  },
  {
    id: 'a2_feedback_sources',
    variants: {
      practical: "Van wie krijg je concrete reacties of vragen?",
      neutral: "Waar komt feedback vandaan?",
      reflective: "Waar voel je dat je wordt gespiegeld?"
    }
  }
];

// Vraagblok B — Marktvalidatie
const BLOCK_B_QUESTIONS: ReflectionQuestion[] = [
  {
    id: 'b1_reality_testing',
    variants: {
      practical: "Waar toets je of dit werkt in de praktijk?",
      neutral: "Hoe controleer je of aannames kloppen?",
      reflective: "Waar ontmoet je de realiteit van buiten?"
    }
  },
  {
    id: 'b2_validation',
    variants: {
      practical: "Wat heb je al laten zien, getest of gedeeld?",
      neutral: "Welke vormen van validatie zijn er geweest?",
      reflective: "Wat heeft al even mogen bestaan buiten je hoofd?"
    }
  }
];

// Vraagblok C — Introspectie & Zelfinzicht
// Deze vragen helpen bij het genereren van realistische mijlpalen
// Vraag 3 kan direct een actie worden ("Wat kun je vandaag doen...")
const BLOCK_C_QUESTIONS: ReflectionQuestion[] = [
  {
    id: 'c1_challenge',
    variants: {
      practical: "Welke uitdaging of onzekerheid zie je op jouw pad richting dit doel?",
      neutral: "Welke obstakels of onzekerheden merk je op onderweg naar dit doel?",
      reflective: "Waar voel je weerstand of twijfel als je aan dit doel denkt?"
    }
  },
  {
    id: 'c2_blocking_patterns',
    variants: {
      practical: "Welke patronen of gewoontes heb je waarvan je weet dat ze je belemmeren, ook al geef je dat niet altijd toe?",
      neutral: "Welke gewoontes of gedragspatronen werken tegen je, ook als je ze niet graag erkent?",
      reflective: "Welke patronen in jezelf houd je liever verborgen, maar weet je dat ze er zijn?"
    }
  },
  {
    id: 'c3_pattern_breaker',
    variants: {
      practical: "Wat kun je vandaag al concreet doen om één van deze patronen te doorbreken?",
      neutral: "Welke concrete stap zou je vandaag kunnen zetten om een patroon te doorbreken?",
      reflective: "Wat zou er gebeuren als je vandaag één klein ding anders doet?"
    }
  },
  {
    id: 'c4_external_help',
    variants: {
      practical: "Heb je al nagedacht over het inschakelen van hulp, zoals een coach, mentor of sparringpartner?",
      neutral: "Overweeg je ondersteuning van anderen, zoals een coach of mentor?",
      reflective: "Wie zou je kunnen helpen zien wat je zelf nog niet ziet?"
    }
  },
  {
    id: 'c5_biggest_challenge',
    variants: {
      practical: "Welk onderdeel van je doel denk je dat de grootste uitdaging wordt?",
      neutral: "Welk aspect van dit doel vraagt de meeste energie of aandacht?",
      reflective: "Waar voel je de meeste spanning als je aan de weg vooruit denkt?"
    }
  },
  {
    id: 'c6_intrinsic_motivation',
    variants: {
      practical: "Waarom is dit doel voor jou echt belangrijk? Wat is je intrinsieke motivatie?",
      neutral: "Wat maakt dit doel persoonlijk betekenisvol voor jou?",
      reflective: "Wat raakt dit doel in wie jij werkelijk bent?"
    }
  }
];

export const REFLECTION_BLOCKS: ReflectionBlock[] = [
  {
    title: "Contact met buitenwereld",
    questions: BLOCK_A_QUESTIONS
  },
  {
    title: "Marktvalidatie",
    questions: BLOCK_B_QUESTIONS
  },
  {
    title: "Introspectie & Zelfinzicht",
    questions: BLOCK_C_QUESTIONS
  }
];

// -----------------------------------------------------------------------------
// VARIANT SELECTION LOGIC
// -----------------------------------------------------------------------------

/**
 * Select appropriate language variant based on goal description
 * OR user preference if provided.
 * 
 * - Practical: concrete, action-oriented goals
 * - Neutral: default, rational approach
 * - Reflective: vague, abstract, intuitive goals
 * 
 * @param goalDescription - The goal description to analyze
 * @param userPreference - Optional user preference (from useLanguageVariant)
 * @returns Selected variant
 */
export function selectVariant(
  goalDescription?: string,
  userPreference?: ReflectionVariant
): ReflectionVariant {
  // If user has explicit preference, use it
  if (userPreference) {
    return userPreference;
  }

  // Otherwise, auto-detect based on goal description
  if (!goalDescription || goalDescription.trim().length < 20) {
    return 'neutral';  // Default for missing/short descriptions
  }

  const desc = goalDescription.toLowerCase();
  
  // Indicators for practical variant
  const practicalKeywords = [
    'klanten', 'omzet', 'verkoop', 'product', 'dienst', 'lanceren',
    'maken', 'bouwen', 'leveren', 'actie', 'stap', 'concreet'
  ];
  
  // Indicators for reflective variant
  const reflectiveKeywords = [
    'voelen', 'intuïtie', 'betekenis', 'essens', 'authentiek', 'innerlijk',
    'energie', 'flow', 'afstemmen', 'verkennen', 'ontdekken'
  ];

  const practicalScore = practicalKeywords.filter(kw => desc.includes(kw)).length;
  const reflectiveScore = reflectiveKeywords.filter(kw => desc.includes(kw)).length;

  if (practicalScore > reflectiveScore && practicalScore >= 2) {
    return 'practical';
  } else if (reflectiveScore > practicalScore && reflectiveScore >= 2) {
    return 'reflective';
  }

  return 'neutral';  // Default
}

// -----------------------------------------------------------------------------
// PROMPT GENERATION FOR AI
// -----------------------------------------------------------------------------

export interface ReflectionPromptData {
  intro: string;
  blocks: Array<{
    title: string;
    questions: string[];
  }>;
  outro: string;
}

/**
 * Generate reflection questions for a specific language variant
 */
export function generateReflectionQuestions(variant: ReflectionVariant): ReflectionPromptData {
  return {
    intro: INTRO_TEXT,
    blocks: REFLECTION_BLOCKS.map(block => ({
      title: block.title,
      questions: block.questions.map(q => q.variants[variant])
    })),
    outro: OUTRO_TEXT
  };
}

/**
 * Generate AI system prompt for reflection mode
 */
export function getReflectionSystemPrompt(): string {
  return `ROL:
Je bent een neutrale begeleider voor ondernemers.
Je analyseert NIET, labelt NIET en trekt GEEN conclusies.
Je stelt uitsluitend vragen om reflectie te ondersteunen.

CONTEXT:
Er bestaat een vaste Focus Beslislaag (beslis → richting → focus).
Deze logica staat vast en mag NIET worden aangepast, verklaard of geïnterpreteerd.
Deze vragenlijst is GEEN detectiemechanisme en beïnvloedt de focuslogica niet.

DOEL:
De ondernemer laten reflecteren op externe feedback, zichtbaarheid en markttoetsing,
zodat eventuele mijlpalen rond "Zichtbaarheid & Markt" logisch en vrijwillig kunnen ontstaan.

BELANGRIJK:
- GEEN woorden als: blinde vlek, tekort, fout, probleem, diagnose
- GEEN scores, percentages of prioriteiten
- GEEN adviezen of oplossingen
- GEEN conclusies trekken
- GEEN koppeling maken met focus, intenties of categorieën

ALLEEN:
- Open vragen stellen
- Rustige, menselijke taal gebruiken
- Reflectie faciliteren

UITVOER:
- Toon alleen vragen
- Geen samenvatting
- Geen interpretatie
- Geen suggesties
- Geen vervolgactie afdwingen`;
}

/**
 * Generate formatted reflection questions as text for AI to output
 */
export function formatReflectionQuestionsForAI(variant: ReflectionVariant): string {
  const data = generateReflectionQuestions(variant);
  
  let output = data.intro + '\n\n';
  
  data.blocks.forEach((block, blockIndex) => {
    // Add special intro for introspection block
    if (block.title === "Introspectie & Zelfinzicht") {
      output += `---\n\n${INTROSPECTION_INTRO_TEXT}\n\n`;
    }
    
    output += `**${block.title}**\n\n`;
    block.questions.forEach((question, qIndex) => {
      output += `${blockIndex + 1}.${qIndex + 1} ${question}\n\n`;
    });
  });
  
  output += data.outro;
  
  return output;
}
